import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Bot, User } from "lucide-react";
import { CalBookingButton } from "./CalBookingButton";

interface ChatMessageProps {
  role: "user" | "assistant";
  content: string;
}

// Strip the booking marker from content and detect if we should show the button
function parseContent(content: string): { text: string; showBooking: boolean } {
  const bookingMarker = "[SHOW_BOOKING]";
  if (content.includes(bookingMarker)) {
    return {
      text: content.replace(bookingMarker, "").trim(),
      showBooking: true,
    };
  }
  return { text: content, showBooking: false };
}

// Parse markdown-style links and render as clickable elements
function renderContentWithLinks(text: string): React.ReactNode {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    const linkText = match[1];
    const linkUrl = match[2];
    
    // Use React Router Link for internal paths, regular anchor for external
    if (linkUrl.startsWith("/")) {
      parts.push(
        <Link
          key={`link-${keyIndex++}`}
          to={linkUrl}
          className="text-primary underline hover:text-primary/80 font-medium"
        >
          {linkText}
        </Link>
      );
    } else {
      parts.push(
        <a
          key={`link-${keyIndex++}`}
          href={linkUrl}
          className="text-primary underline hover:text-primary/80 font-medium"
        >
          {linkText}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  // If no links found, return original text
  if (parts.length === 0) {
    return text;
  }

  return <>{parts}</>;
}

export const ChatMessage = React.forwardRef<HTMLDivElement, ChatMessageProps>(
  ({ role, content }, ref) => {
    const isAssistant = role === "assistant";
    const { text, showBooking } = parseContent(content);

    return (
      <div
        ref={ref}
        className={cn(
          "flex gap-3",
          isAssistant ? "justify-start" : "justify-end"
        )}
      >
        {isAssistant && (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bot className="h-4 w-4" />
          </div>
        )}
        <div className="flex max-w-[80%] flex-col gap-2">
          <div
            className={cn(
              "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
              isAssistant
                ? "bg-muted text-foreground rounded-tl-sm"
                : "bg-primary text-primary-foreground rounded-tr-sm"
            )}
          >
            <div className="whitespace-pre-wrap break-words">{renderContentWithLinks(text)}</div>
          </div>
          {showBooking && isAssistant && (
            <CalBookingButton className="self-start" />
          )}
        </div>
        {!isAssistant && (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <User className="h-4 w-4" />
          </div>
        )}
      </div>
    );
  }
);

ChatMessage.displayName = "ChatMessage";
